// Custom Block Library — three independent access gates (pure logic).
//   library       — show the library + allow insert-from-library (Phase 1).
//   aiCatalog     — let the AI generator reference library blocks (Phase 3; off by default).
//   optimizerPool — let the optimizer mutate library blocks (Phase 4; off by default).
// AI/optimizer FAIL CLOSED. The library has a local per-browser kill + a global env override.

import type { BlockLibraryFlagsState } from './blockLibraryRemoteKill';
import { publicEnv } from './clientEnv';

export type BlockGate = { enabled: boolean; reason: string };
export interface BlockLibraryGates { library: BlockGate; aiCatalog: BlockGate; optimizerPool: BlockGate; }

export const BLOCK_LIBRARY_KILL_KEY = 'block_library_kill';

function ls(key: string): string | null { try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; } }

export function blockLibraryGate(opts: { state?: BlockLibraryFlagsState } = {}): BlockLibraryGates {
  const s = opts.state;
  const off = (reason: string): BlockGate => ({ enabled: false, reason });

  // ── library tier ──
  let library: BlockGate;
  if (ls(BLOCK_LIBRARY_KILL_KEY) === '1') library = off('local_kill');
  else if ((publicEnv('VITE_BLOCK_LIBRARY') ?? '').toLowerCase() === 'off') library = off('disabled_globally');
  else if (s?.configured) library = s.loaded && !s.error && s.library ? { enabled: true, reason: 'remote_on' } : off(s.error ? 'remote_unavailable' : 'remote_off');
  else library = { enabled: true, reason: 'default_on' }; // curated-only is safe

  // ── ai-catalog / optimizer-pool tiers (fail closed; require library on) ──
  const aiCatalog = library.enabled && s?.loaded && !s.error && s.aiCatalog ? { enabled: true, reason: 'remote_on' } : off(!library.enabled ? 'library_off' : 'fail_closed');
  const optimizerPool = library.enabled && s?.loaded && !s.error && s.optimizerPool ? { enabled: true, reason: 'remote_on' } : off(!library.enabled ? 'library_off' : 'fail_closed');

  return { library, aiCatalog, optimizerPool };
}

export function engageBlockLibraryKill(on: boolean): void {
  try { if (on) localStorage.setItem(BLOCK_LIBRARY_KILL_KEY, '1'); else localStorage.removeItem(BLOCK_LIBRARY_KILL_KEY); } catch { /* ignore */ }
}
